import {
  Box,
  Card,
  CardContent,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  useMediaQuery,
  useTheme
} from "@mui/material";

// Skeleton for user info card
export const userInfoCardSkeleton = () => {
  return (
    <Card className="pagelist-content" sx={{ height: "130px" }}>
      <Stack sx={{ pr: 2,  pl: 2,  p: 1 }}   spacing={2}>
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <Stack>
            <Skeleton variant="text" width={60} height={32} sx={{ bgcolor: "grey.500" }} />
            <Skeleton variant="text" width={180} height={32} sx={{ bgcolor: "grey.500" }} />
          </Stack>
          <Skeleton variant="circular" width={56} height={56} sx={{ bgcolor: "grey.500" }} />
        </Stack>
        <Stack sx={{ marginTop: "10px",  justifyContent: "space-between", alignItems: "center" }} direction="row" >
          <Stack sx={{ alignItems: "center" }} spacing={1} direction="row">
            <Skeleton variant="text" width={40} height={32} sx={{ bgcolor: "grey.500" }} />
            <Skeleton variant="text" width={80} height={24} sx={{ bgcolor: "grey.500" }} />
          </Stack>
          <Skeleton
            variant="rectangular"
            width={120}
            height={30}
            sx={{ borderRadius: "7px", bgcolor: "grey.500" }}
          />
        </Stack>
      </Stack>
    </Card>
  );
};

// Skeleton for build id info card
export const buildIdInfoCardSkeleton = () => {
  return (
    <Card className="pagelist-content" sx={{ height: "130px", cursor: "pointer" }}>
      <Stack sx={{ p: 1.5, alignItems: "center" }} spacing={1} direction={"row"}>
        <Skeleton variant="circular" width={48} height={40} sx={{ bgcolor: "grey.500" }} />
        <Skeleton variant="text" width={800} height={40} sx={{ bgcolor: "grey.500" }} />
      </Stack>
    </Card>
  );
};

export const questionsSkeleton = () => {
  return (
    <Card className="pagelist-content" sx={{ minHeight: 220 }}>
      <CardContent>
        <Stack spacing={2}>
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="rectangular" width="100%" height={40} />
          <Skeleton variant="rectangular" width="100%" height={40} />
          <Skeleton variant="rectangular" width="100%" height={40} />
          <Skeleton variant="text" width="40%" height={24} />
        </Stack>
      </CardContent>
    </Card>
  );
};

// Skeleton for site card
export const SiteCardSkeleton = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <Card className="pagelist-content" sx={{ height: isSmallScreen ? "330px" : "400px" }}>
      <Stack sx={{ p: 1.5 }} spacing={1}>
        <Skeleton variant="text" width={180} height={32} sx={{ bgcolor: "grey.500" }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2.2
          }}>
          {[1, 2, 3, 4, 5].map((_, idx) => (
            <Card
              className="pagelist-content"
              key={idx}
              sx={{
                width: isSmallScreen ? 200 : 300,
                flex: "0 0 auto"
              }}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={isSmallScreen ? 160 : 195}
                sx={{ bgcolor: "grey.500" }}
              />
              <Box sx={{ p: 2 }}>
                <Skeleton variant="text" width="80%" height={24} sx={{ bgcolor: "grey.500" }} />
                <Skeleton variant="text" width="60%" height={20} sx={{ bgcolor: "grey.500" }} />
                <Skeleton variant="text" width="40%" height={20} sx={{ bgcolor: "grey.500" }} />
              </Box>
            </Card>
          ))}
        </Box>
        <Box sx={{ justifyContent: "center",  display: "flex" }} >
          <Skeleton
            variant="rectangular"
            width={180}
            height={36}
            sx={{ borderRadius: "7px", bgcolor: "grey.500" }}
          />
        </Box>
      </Stack>
    </Card>
  );
};

// Skeleton for TimesheetCard
export const timesheetCardSkeleton = () => {
  return (
    <Card
      className="pagelist-content"
      sx={{ height: "350px", display: "flex", flexDirection: "column" }}>
      <Stack sx={{ p: 2, flexGrow: 1, flexBasis: 0 }} spacing={1}>
        <Stack sx={{ justifyContent: "space-between", alignItems: "center" }} direction="row">
          <Skeleton variant="text" width={120} height={32} sx={{ bgcolor: "grey.500" }} />
          <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor: "grey.500" }} />
        </Stack>
        <Box sx={{ flexGrow: 1, minHeight: 0, overflow: "auto" }}>
          <Stack spacing={1}>
            {[1, 2, 3].map((_, idx) => (
              <Card key={idx} className="pagelist-content">
                <ListItem disablePadding>
                  <ListItemButton>
                    <ListItemText primary={ <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                          <Skeleton
                            variant="text"
                            width={120}
                            height={24}
                            sx={{ bgcolor: "grey.500" }}
                          />
                          <Stack direction="row" spacing={2}>
                            <Stack sx={{ alignItems: "center" }}>
                              <Skeleton
                                variant="text"
                                width={40}
                                height={18}
                                sx={{ bgcolor: "grey.500" }}
                              />
                              <Skeleton
                                variant="text"
                                width={30}
                                height={18}
                                sx={{ bgcolor: "grey.500" }}
                              />
                            </Stack>
                            <Stack sx={{ alignItems: "center" }}>
                              <Skeleton
                                variant="text"
                                width={40}
                                height={18}
                                sx={{ bgcolor: "grey.500" }}
                              />
                              <Skeleton
                                variant="text"
                                width={50}
                                height={18}
                                sx={{ bgcolor: "grey.500" }}
                              />
                            </Stack>
                          </Stack>
                        </Stack>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              </Card>
            ))}
          </Stack>
        </Box>
        <Box sx={{ mt: 2,  justifyContent: "center",  display: "flex" }}  >
          <Skeleton
            variant="rectangular"
            width={120}
            height={36}
            sx={{ borderRadius: "15px", bgcolor: "grey.500" }}
          />
        </Box>
      </Stack>
    </Card>
  );
};

// Skeleton for ExpensesCard
export const expensesCardSkeleton = () => {
  return (
    <Card className="pagelist-content" sx={{ height: "350px" }}>
      <Stack sx={{ p: 2 }} spacing={1}>
        <Stack sx={{ justifyContent: "space-between", alignItems: "center" }} direction="row">
          <Skeleton variant="text" width={120} height={32} sx={{ bgcolor: "grey.500" }} />
          <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor: "grey.500" }} />
        </Stack>
        <Stack spacing={1.5}>
          {[1, 2, 3].map((_, idx) => (
            <Card key={idx} className="pagelist-content">
              <ListItem disablePadding>
                <ListItemButton>
                  <ListItemText
                    primary={
                      <Skeleton
                        variant="text"
                        width={120}
                        height={24}
                        sx={{ bgcolor: "grey.500" }}
                      />
                    }
                    secondary={
                      <Skeleton
                        variant="text"
                        width={80}
                        height={15}
                        sx={{ bgcolor: "grey.500" }}
                      />
                    }
                  />
                  <Skeleton
                    variant="text"
                    width={60}
                    height={24}
                    sx={{ bgcolor: "grey.500", ml: 2 }}
                  />
                </ListItemButton>
              </ListItem>
            </Card>
          ))}
          <Box sx={{ mt: 2,  justifyContent: "center",  display: "flex" }}  >
            <Skeleton
              variant="rectangular"
              width={120}
              height={36}
              sx={{ borderRadius: "15px", bgcolor: "grey.500" }}
            />
          </Box>
        </Stack>
      </Stack>
    </Card>
  );
};

// Skeleton for CompanyList from Home
export const CompanyListSkeleton = () => {
  return (
    <Stack sx={{ gap: 1 }} >
      <Stack sx={{ display: "flex",  justifyContent: "space-between", alignItems: "center" }}  direction={"row"}>
        <Skeleton variant="text" width={100} height={28} />
        <Skeleton variant="rectangular" width={120} height={36} />
      </Stack>
      <Stack spacing={1}>
        {[...Array(3)].map((_, idx) => (
          <Card key={idx} className="pagelist-content">
            <ListItem style={{ padding: "0px" }}>
              <ListItemButton>
                <Skeleton variant="text" width="80%" height={24} />
              </ListItemButton>
              {/* Three-dot skeleton */}
              <Stack sx={{ alignItems: "center", ml: 1, mx: 2 }} direction="column" spacing={0.5}>
                <Skeleton variant="circular" width={6} height={6} />
                <Skeleton variant="circular" width={6} height={6} />
                <Skeleton variant="circular" width={6} height={6} />
              </Stack>
            </ListItem>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
};

// Skeleton for CompaniesSkeleton
export const CompaniesSkeleton = () => {
  return (
    <Stack sx={{ gap: 1 }} >
      {[...Array(11)].map((_, idx) => (
        <Card key={idx} className="pagelist-content">
          <ListItem disablePadding>
            <ListItemButton>
              <ListItemText primary={<Skeleton variant="text" width="99%" height={30} />} />
            </ListItemButton>
          </ListItem>
        </Card>
      ))}
    </Stack>
  );
};

// Skeleton for ProductsSkeleton
export const ProductsSkeleton = () => {
  return (
    <Stack sx={{ gap: 1 }} >
      {[...Array(11)].map((_, idx) => (
        <Card key={idx} className="pagelist-content">
          <ListItem disablePadding>
            <ListItemButton>
              <ListItemText
                primary={<Skeleton variant="text" width="99%" height={28} />}
                secondary={<Skeleton variant="text" width="30%" height={20} />}
              />
            </ListItemButton>
          </ListItem>
        </Card>
      ))}
    </Stack>
  );
};

